///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Native shell startup: status bar, splash screen hand-off, deep links and
/// the Android back button. Called from main.tsx; every function is a
/// no-op in a browser so the web build pays nothing for it.
///

import { App, type URLOpenListenerEvent } from '@capacitor/app'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { initNativeAuth } from './auth'
import { loadNativeDeviceInfo } from './device'
import { isNativeApp, nativePlatform } from './platform'

let splashHidden = false

/**
 * One-time native setup. Runs before React mounts so the status bar and
 * listeners are in place for the first frame.
 */
export async function initNative(): Promise<void> {
	if (!isNativeApp()) return

	// Draw under the status bar; the layout already honors safe-area insets
	// (viewport-fit=cover in index.html)
	try {
		await StatusBar.setOverlaysWebView({ overlay: true })
		await StatusBar.setStyle({ style: Style.Dark })
	} catch {
		// Plugin absent in a partially synced build; cosmetic only
	}

	// Device description headers and the native sign-in providers / passkey
	// shim. Neither blocks first paint; sign-in awaits initNativeAuth itself
	void loadNativeDeviceInfo()
	void initNativeAuth()

	// Universal Links / App Links (https://earthbrowser.com/...?target=...)
	// arrive here instead of as a page load. Reuse the same
	// ?lat=&lon=&target=&layers= parsing the web app does by pushing the
	// in-app location and letting BrowserRouter re-read it.
	void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
		handleUrlOpen(event.url)
	})

	// Android hardware back: an open Radix dialog closes first (it listens
	// for Escape on the document), then react-router history; with nothing
	// left to pop we hand the gesture to the system (minimizes, doesn't kill)
	if (nativePlatform() === 'android') {
		void App.addListener('backButton', ({ canGoBack }) => {
			if (document.querySelector('[role="dialog"][data-state="open"]')) {
				document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
				return
			}
			if (canGoBack) {
				window.history.back()
				return
			}
			void App.minimizeApp()
		})
	}
}

/**
 * Hide the native splash once the app has painted. The config sets
 * launchAutoHide: false so the slate splash stays up until the globe has a
 * first frame instead of flashing an empty page.
 */
export function hideSplash(): void {
	if (splashHidden || !isNativeApp()) return
	splashHidden = true
	// Two frames: one for React's commit, one for the engine's first draw
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			void SplashScreen.hide({ fadeOutDuration: 250 })
		})
	})
}

/** Route an incoming URL (universal link) as an in-app deep link */
function handleUrlOpen(url: string): void {
	let incoming: URL
	try {
		incoming = new URL(url)
	} catch {
		return
	}
	applyDeepLink(`${incoming.pathname}${incoming.search}${incoming.hash}`)
}

/**
 * Apply an app-relative URL (path + query + hash) without a reload. The
 * host of a universal link is dropped; the bundle's own origin stays.
 */
function applyDeepLink(target: string): void {
	if (target === `${window.location.pathname}${window.location.search}${window.location.hash}`) return
	window.history.pushState(null, '', target)
	// react-router listens for popstate, not pushState; nudge it
	window.dispatchEvent(new PopStateEvent('popstate'))
}
